import { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import {
  getSubscriptions,
  getUserDetails,
} from '../../services/adminService';
import { DaysOfTheWeekTrans, formatDateLocalized } from '../../utils/translations';
import { formatPassPriceShort } from '../../utils/currencyFormat';
import UserDetailsModal from './users/UserDetailsModal';

export default function AdminSubscriptions({ token }) {
  const { language } = useAppContext();
  const daysTrans = DaysOfTheWeekTrans[language];

  const formatDate = (d) => formatDateLocalized(d, daysTrans);
  const [subscriptions, setSubscriptions] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [page, setPage] = useState(1);
  const [includeTest, setIncludeTest] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [passTypes, setPassTypes] = useState([]);
  const [passTypeFilter, setPassTypeFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [expandedRenewalUsers, setExpandedRenewalUsers] = useState(() => new Set());

  useEffect(() => {
    fetchSubscriptions();
  }, [page, includeTest, statusFilter, passTypeFilter]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const data = await getSubscriptions(token, page, includeTest, statusFilter, passTypeFilter);
      setSubscriptions(data.subscriptions || []);
      setRenewals(data.renewals || []);
      setPassTypes(data.passTypes || []);
      setPagination(data.pagination || null);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRenewalUser = (userKey) => {
    setExpandedRenewalUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userKey)) next.delete(userKey);
      else next.add(userKey);
      return next;
    });
  };

  const renewalsByUser = renewals.reduce((groups, renewal) => {
    const userKey = renewal.userId?.user_id || renewal.userId?._id || 'unknown';
    let group = groups.find((g) => g.userKey === userKey);
    if (!group) {
      group = { userKey, userId: renewal.userId, renewals: [] };
      groups.push(group);
    }
    group.renewals.push(renewal);
    return groups;
  }, []);

  const handleViewUser = async (userId) => {
    if (!userId) return;
    setSelectedUserId(userId);
    setDetailsLoading(true);
    try {
      const data = await getUserDetails(token, userId);
      setUserDetails(data);
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeUserModal = () => {
    setSelectedUserId(null);
    setUserDetails(null);
  };

  const subStatusBadge = (sub) => (
    <span className={`px-3 py-1 rounded text-xs font-semibold ${sub.subscriptionActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
      {sub.subscriptionActive ? 'active' : 'inactive'}
    </span>
  );

  const renewalStatusBadge = (renewal) => (
    <span className={`px-3 py-1 rounded text-xs font-semibold ${
      renewal.status === 'successful' ? 'bg-green-100 text-green-700' :
      renewal.status === 'failed' ? 'bg-red-100 text-red-700' :
      'bg-blue-100 text-blue-700'
    }`}>
      {renewal.status}
    </span>
  );

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-8">
      {/* Subscriptions (from Pass with isSubscription: true) */}
      <section>
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3 mb-3">
          <h3 className="text-lg font-semibold">Subscriptions</h3>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 shrink-0">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm min-w-0"
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 shrink-0">Pass type:</label>
              <select
                value={passTypeFilter}
                onChange={(e) => {
                  setPassTypeFilter(e.target.value);
                  setPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm min-w-0"
              >
                <option value="">All</option>
                {passTypes
                  .slice()
                  .sort((a, b) => {
                    const an = (a.displayName || a.name || '').toString();
                    const bn = (b.displayName || b.name || '').toString();
                    return an.localeCompare(bn);
                  })
                  .map((pt) => (
                    <option key={pt._id} value={pt._id}>
                      {pt.displayName || pt.name}
                    </option>
                  ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={includeTest}
                onChange={(e) => {
                  setIncludeTest(e.target.checked);
                  setPage(1);
                }}
              />
              Include test passes
            </label>
          </div>
        </div>

        {/* Desktop: Active subs table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left text-sm min-w-[640px]">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="py-3 px-4 font-semibold">User</th>
                <th className="py-3 px-4 font-semibold">Pass Type</th>
                <th className="py-3 px-4 font-semibold">Renewal #</th>
                <th className="py-3 px-4 font-semibold">Price</th>
                <th className="py-3 px-4 font-semibold">Next billing</th>
                <th className="py-3 px-4 font-semibold">Purchased</th>
                <th className="py-3 px-4 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub) => (
                <tr key={sub._id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <button
                      type="button"
                      onClick={() => handleViewUser(sub.userId?.user_id)}
                      className="text-left text-blue-600 hover:underline cursor-pointer font-medium"
                    >
                      {sub.userId?.name || sub.userId?.email || 'N/A'}
                    </button>
                  </td>
                  <td className="py-3 px-4">{sub.passTypeId?.name || sub.passTypeId?.displayName || 'N/A'}</td>
                  <td className="py-3 px-4">#{sub.renewalNumber ?? 0}</td>
                  <td className="py-3 px-4">
                    {formatPassPriceShort(sub.finalPrice, sub.currency)}
                  </td>
                  <td className="py-3 px-4 text-xs">{formatDate(sub.nextBillingDate) ?? '—'}</td>
                  <td className="py-3 px-4 text-xs">{formatDate(sub.purchasedAt) ?? 'N/A'}</td>
                  <td className="py-3 px-4">
                    {subStatusBadge(sub)}
                    {sub.isAdmin && (
                      <span className="ml-1 px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">Test</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: Active subs cards */}
        <div className="md:hidden space-y-3">
          {subscriptions.map((sub) => (
            <div key={sub._id} className="border border-gray-200 rounded-lg p-4 bg-white space-y-2">
              <div className="flex justify-between items-start gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => handleViewUser(sub.userId?.user_id)}
                  className="text-left text-blue-600 hover:underline font-medium text-sm"
                >
                  {sub.userId?.name || sub.userId?.email || 'N/A'}
                </button>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {subStatusBadge(sub)}
                  {sub.isAdmin && (
                    <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">Test</span>
                  )}
                </div>
              </div>
              <p className="text-sm font-medium text-gray-800">{sub.passTypeId?.name || sub.passTypeId?.displayName || 'N/A'}</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                <span>Renewal #</span>
                <span>#{sub.renewalNumber ?? 0}</span>
                <span>Price</span>
                <span>{formatPassPriceShort(sub.finalPrice, sub.currency)}</span>
                <span>Next billing</span>
                <span>{formatDate(sub.nextBillingDate) ?? '—'}</span>
                <span>Purchased</span>
                <span>{formatDate(sub.purchasedAt) ?? 'N/A'}</span>
              </div>
            </div>
          ))}
        </div>

        {subscriptions.length === 0 && !loading && <p className="text-center py-4 text-gray-500 text-sm">No active subscriptions</p>}
      </section>

      {/* Renewals (from SubscriptionRenewal), collapsed by user */}
      <section>
        <h3 className="text-lg font-semibold mb-3">Renewal history</h3>

        <div className="space-y-2">
          {renewalsByUser.map((group) => {
            const isExpanded = expandedRenewalUsers.has(group.userKey);
            return (
              <div key={group.userKey} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleRenewalUser(group.userKey)}
                  className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                    <span className="font-medium text-sm truncate">
                      {group.userId?.name || group.userId?.email || 'N/A'}
                    </span>
                  </span>
                  <span className="text-xs text-gray-500 shrink-0">
                    {group.renewals.length} renewal{group.renewals.length === 1 ? '' : 's'}
                  </span>
                </button>

                {isExpanded && (
                  <div className="p-3">
                    {/* Desktop: Renewals table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-left text-sm min-w-[560px]">
                        <thead className="border-b border-gray-200 bg-gray-50">
                          <tr>
                            <th className="py-2 px-3 font-semibold">Pass Type</th>
                            <th className="py-2 px-3 font-semibold">Renewal #</th>
                            <th className="py-2 px-3 font-semibold">Price</th>
                            <th className="py-2 px-3 font-semibold">Period</th>
                            <th className="py-2 px-3 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.renewals.map((renewal) => (
                            <tr key={renewal._id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-2 px-3">{renewal.passId?.passTypeId?.name || renewal.passId?.passTypeId?.displayName || 'N/A'}</td>
                              <td className="py-2 px-3">#{renewal.renewalNumber}</td>
                              <td className="py-2 px-3">
                                {formatPassPriceShort(renewal.renewalPrice, renewal.passId?.currency)}
                              </td>
                              <td className="py-2 px-3 text-xs">
                                {formatDate(renewal.periodStartDate)} – {formatDate(renewal.periodEndDate)}
                              </td>
                              <td className="py-2 px-3">{renewalStatusBadge(renewal)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile: Renewals cards */}
                    <div className="md:hidden space-y-3">
                      {group.renewals.map((renewal) => (
                        <div key={renewal._id} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2">
                          <div className="flex justify-between items-start gap-2 flex-wrap">
                            <p className="text-sm font-medium text-gray-800">{renewal.passId?.passTypeId?.name || renewal.passId?.passTypeId?.displayName || 'N/A'}</p>
                            {renewalStatusBadge(renewal)}
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                            <span>Renewal #</span>
                            <span>#{renewal.renewalNumber}</span>
                            <span>Price</span>
                            <span>{formatPassPriceShort(renewal.renewalPrice, renewal.passId?.currency)}</span>
                            <span>Period</span>
                            <span>{formatDate(renewal.periodStartDate)} – {formatDate(renewal.periodEndDate)}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => handleViewUser(group.userId?.user_id)}
                        className="text-xs text-blue-600 hover:underline cursor-pointer font-medium"
                      >
                        View user details
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {loading && <p className="text-center py-4 text-sm">Loading...</p>}
        {renewals.length === 0 && !loading && <p className="text-center py-4 text-gray-500 text-sm">No renewals found</p>}

        {pagination && pagination.pages > 1 && (
          <div className="flex flex-wrap gap-2 mt-4 justify-center items-center">
            <button
              onClick={() => page > 1 && setPage(page - 1)}
              disabled={page === 1}
              className="px-3 py-2 sm:px-4 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
            >
              Previous
            </button>
            <span className="px-2 sm:px-4 py-2 text-sm">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => page < pagination.pages && setPage(page + 1)}
              disabled={page >= pagination.pages}
              className="px-3 py-2 sm:px-4 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
            >
              Next
            </button>
          </div>
        )}
      </section>

      {selectedUserId && (
        <UserDetailsModal
          userDetails={userDetails}
          detailsLoading={detailsLoading}
          onClose={closeUserModal}
          onRefresh={() => { closeUserModal(); fetchSubscriptions(); }}
          token={token}
        />
      )}
    </div>
  );
}